<template>
  <div
    class="insights-cta-banner"
    :class="computedClasses"
  >
    <i
      v-if="allowClose"
      class="hi hi-close hi-10"
      @click="close"
    />
    <section class="info">
      <h4 class="title">{{ title }}</h4>
      <p class="description">{{ description }}</p>
      <ul v-if="features.length">
        <li
          v-for="feature in features"
          :key="feature"
          class="d-flex align-items-center gap-5"
        >
          <span :class="`dot dot-${type}`">&middot;</span>
          <span class="feature-title">{{ feature }}</span>
        </li>
      </ul>
      <div class="cta">
        <AppButton
          v-if="!hideActionButton"
          size="sm"
          :class="`submit-button-${type}`"
          :type="bannersContent.ctaType[type]"
          :disabled="isLoading"
          @click.prevent="submit"
        >
          {{ bannersContent.copy[type].cta }}
        </AppButton>
        <AppButton
          v-if="showLearnMore"
          size="sm"
          :class="`btn-outline-custom btn-outline-custom-${type}`"
          @click="showDialog"
        >
          Learn more
        </AppButton>
        <div
          v-if="allowClose"
          :class="`dismiss dismiss-${type}`"
        >
          <AppCheckbox
            :value="dismiss"
            @input="setDismiss"
          >
            Don’t show this again
          </AppCheckbox>
        </div>
      </div>
    </section>
    <img
      class="illustration"
      :src="bannersAssets.illustration[type].src"
      :alt="bannersAssets.illustration[type].alt"
    />
  </div>
</template>

<script>
import { INSIGHTS as insightsConstants, INSIGHTS_ASSETS as insightsAssets } from '~/app_constants';
import AppButton from '~/components/AppButton.vue';
import AppCheckbox from '~/components/AppCheckbox.vue';

const SLIM_BREAKPOINT = 660;

export default {
  components: {
    AppButton,
    AppCheckbox,
  },
  props: {
    type: {
      type: String,
      required: true,
      validator: (value) => ['upgrade', 'enable', 'workforce', 'suspicious_behavior'].includes(value),
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    hideActionButton: {
      type: Boolean,
      default: false,
    },
    allowClose: {
      type: Boolean,
      default: true,
    },
    customContent: {
      type: Object,
      default: () => {},
    },
    showLearnMore: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    dismiss: false,
    bannersAssets: insightsAssets.banners,
    bannersContent: insightsConstants.banners,
    resizeObserver: null,
    isSlim: false,
  }),
  computed: {
    computedClasses() {
      return {
        [`insights-cta-banner-${this.type}`]: true,
        'insights-cta-banner-slim': this.isSlim,
      };
    },
    title() {
      return this.customContent?.title ? this.customContent.title : this.bannersContent.copy[this.type].title;
    },
    description() {
      return this.customContent?.description
        ? this.customContent.description
        : this.bannersContent.copy[this.type].description;
    },
    features() {
      return this.customContent?.features || [];
    },
  },
  mounted() {
    this.setupResizeObserver();
  },
  beforeDestroy() {
    this.stopResizeObserver();
  },
  methods: {
    setDismiss(val) {
      this.dismiss = val;
      this.$emit('hide', val);
    },
    showDialog() {
      this.$emit('showDialog', true);
    },
    submit() {
      this.$emit('submit');
    },
    close() {
      this.$emit('close', this.dismiss);
    },
    setupResizeObserver() {
      this.resizeObserver = new ResizeObserver((entries) => {
        entries.forEach(this.onResize);
      });
      this.resizeObserver.observe(this.$el);
    },
    onResize() {
      const width = this.$el.clientWidth;

      this.isSlim = width < SLIM_BREAKPOINT;
    },
    stopResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    },
  },
};
</script>
